import { Injectable } from "@angular/core";
import { EndpointService } from "./endpoint.service";
import { HttpClient } from "@angular/common/http";
import {
    SampleRecordsAverageParameter,
    SensorRecordsRequestModel,
} from "../models/sensorRecordsRequest.model";
import { catchError } from "rxjs/operators";
import { of, Observable } from "rxjs";
import { ToastService } from "./toast.service";
import { DataAnalysisRequestModel } from "../models/dataAnalysisRequest.model";
import { SensorAnalysisRecord } from "../models/sensorAnalysisRecord.model";
import { DataAnalysisExportModel } from "../models/dataAnalysisExport.model";
import { AuthService } from "./auth.service";
import { BatteryStatus, RecordDto } from "../models/recordDto.model";
import {
    NodeRecordChartModel,
    SensorRecordChartModel,
} from "../models/sensorRecordChart.model";
import {
    NodeRecordsRequestModel,
    WindDustDistribution,
    NodeSensorRecordsRequestModel,
} from "../models/nodeRecords.model";
import { FilterRequest } from "../models/filterRequest.model";

@Injectable({
    providedIn: "root",
})
export class SensorRecordService extends EndpointService {
    constructor(
        http: HttpClient,
        private toastService: ToastService,
        private authService: AuthService,
    ) {
        super(http);
    }

    getLatestSensorRecordBySensor(sensorId: string) {
        return this.http
            .get<RecordDto>(
                this.getApiUrl(
                    `SensorRecord/GetLatestSensorRecordBySensor?sensorId=${sensorId}`,
                ),
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading latest sensor data",
                        true,
                    );
                    return of(new RecordDto());
                }),
            );
    }

    getParameters(
        payload: DataAnalysisRequestModel,
    ): Observable<SensorAnalysisRecord[]> {
        this.authService.setCurrentUserAnalysisParameters(payload);
        return this.http
            .post<
                SensorAnalysisRecord[]
            >(this.getApiUrl("SensorRecord/GetParameters"), payload)
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading sensor parameters",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    exportParameters(payload: DataAnalysisExportModel): Observable<Blob> {
        this.authService.setCurrentUserAnalysisParameters(payload.models[0]);
        return this.http.post(
            this.getApiUrl("SensorRecord/ExportParameters"),
            payload,
            { responseType: "blob" },
        );
    }

    getSensorRecords(payload: SensorRecordsRequestModel) {
        this.authService.setCurrentUserParameters(payload.parameters);
        return this.http
            .post<SensorRecordChartModel>(
                this.getApiUrl("SensorRecord/GetSensorRecords"),
                payload,
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading sensor data",
                        true,
                    );
                    return of(new SensorRecordChartModel());
                }),
            );
    }

    getLatestNodeRecords(payload: NodeRecordsRequestModel) {
        return this.http
            .post<
                RecordDto[]
            >(this.getApiUrl("SensorRecord/GetLatestNodeRecords"), payload)
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading node data",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getAverageParameterValues(payload: FilterRequest) {
        return this.http
            .post<
                SampleRecordsAverageParameter[]
            >(this.getApiUrl("SensorRecord/GetAverageParameterValues"), payload)
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading node data",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getWindDustDistribution(payload: WindDustDistribution) {
        return this.http
            .post<any>(
                this.getApiUrl("SensorRecord/WindDustDistribution"),
                payload,
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading node data",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getLatestNodeRecordsWithHeatmap(payload: NodeRecordsRequestModel) {
        return this.http
            .post<any>(
                this.getApiUrl("SensorRecord/GetLatestNodeRecordsWithHeatmap"),
                payload,
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading node data",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getBatteryStatus(id: string) {
        return this.http.get<BatteryStatus>(
            this.getApiUrl(`SensorRecord/GetBatteryStatus?id=${id}`),
        );
    }

    getNodeRecords(payload: NodeSensorRecordsRequestModel) {
        return this.http
            .post<NodeRecordChartModel>(
                this.getApiUrl("SensorRecord/GetNodeRecords"),
                payload,
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading node data",
                        true,
                    );
                    return of(new NodeRecordChartModel());
                }),
            );
    }
}
